<template>
  <v-container fluid class="pa-4">
    <!-- Section Rapports Financiers Entreprise Admin -->
    <v-card elevation="2">
      <v-card-title class="white--text d-flex justify-space-between align-center" style="background-color: #607D8B;">
        <div>
          <v-icon left dark>mdi-domain</v-icon>
          Rapports financiers d'une entreprise
        </div>
        <div class="d-flex">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  dark
                  @click="exportToExcel"
                  :loading="exportLoading"
                  class="mr-2"
                  :disabled="filteredTransactions.length === 0"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-file-excel-outline</v-icon>
              </v-btn>
            </template>
            <span>Exporter en Excel</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  dark
                  @click="refreshData"
                  :loading="loading"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Actualiser les données</span>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-alert color="info" text dense class="mb-4">
          <v-icon left small>mdi-information</v-icon>
          Selectionnez un restaurant et une entreprise pour consulter les rapports financiers detaillés.
        </v-alert>

        <!-- Section de recherche -->
        <v-card class="mb-6" elevation="2">
          <v-card-text class="pa-4">
            <v-row align="center" justify="center">
              <v-col cols="12" md="4">
                <v-select
                    v-model="balanceInfos.onlyRestaurantNameSelect"
                    :items="balanceInfos.onlyRestaurantsNamesList"
                    label="Nom restaurant"
                    prepend-icon="mdi-silverware"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="[rules.required]"
                ></v-select>
              </v-col>

              <v-col cols="12" md="4">
                <v-select
                    v-model="balanceInfos.onlyCompanyNameSelect"
                    :items="balanceInfos.onlyCompaniesNamesList"
                    label="Nom entreprise"
                    prepend-icon="mdi-domain"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="[rules.required]"
                ></v-select>
              </v-col>

              <v-col cols="12" md="4">
                <v-btn
                    block
                    height="40px"
                    @click="searchCompany"
                    :disabled="!canSearch"
                    :loading="loading"
                    color="primary"
                >
                  <v-icon class="mr-1">mdi-magnify</v-icon>
                  Rechercher
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section Informations Globales -->
        <v-card class="mb-4" outlined v-if="companyFound">
          <v-card-title class="primary white--text">
            <v-icon left>mdi-chart-line</v-icon>
            Informations Globales - {{ balanceInfos.onlyCompanyNameSelect }} chez {{ balanceInfos.onlyRestaurantNameSelect }}
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <!-- Depenses Globales Mois Actuel -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #2196F3;">
                  <v-icon size="48" color="blue">mdi-trending-up</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.amountDueByCompanyActualMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">Depenses Globales</p>
                  <p class="text-caption grey--text">{{ getCurrentMonthName() }}</p>
                </v-card>
              </v-col>

              <!-- Quote-part Entreprise Mois Actuel -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #FF9800;">
                  <v-icon size="48" color="orange">mdi-domain</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.quotePartCompanyTotalActual }}</h2>
                  <p class="text-subtitle-2 grey--text">Quote-part Entreprise</p>
                  <p class="text-caption grey--text">Mois actuel</p>
                </v-card>
              </v-col>

              <!-- Quote-part Salaries -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #4CAF50;">
                  <v-icon size="48" color="green">mdi-account-cash</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.quotePartSalarialeActual }}</h2>
                  <p class="text-subtitle-2 grey--text">Quote-part Salaries</p>
                  <p class="text-caption grey--text">Mois actuel</p>
                </v-card>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <v-row>
              <!-- Depenses Globales Mois Precedent -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #9C27B0;">
                  <v-icon size="48" color="purple">mdi-history</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.amountDueByCompanyPreviousMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">Depenses Globales</p>
                  <p class="text-caption grey--text">{{ getPreviousMonthName() }}</p>
                </v-card>
              </v-col>

              <!-- Quote-part Entreprise Mois Precedent -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #795548;">
                  <v-icon size="48" color="brown">mdi-office-building</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.quotePartCompanyTotalPrevious }}</h2>
                  <p class="text-subtitle-2 grey--text">Quote-part Entreprise</p>
                  <p class="text-caption grey--text">Mois precedent</p>
                </v-card>
              </v-col>

              <!-- Quote-part Salaries Mois Precedent -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #607D8B;">
                  <v-icon size="48" color="blue-grey">mdi-account-group</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.quotePartSalarialePrevious }}</h2>
                  <p class="text-subtitle-2 grey--text">Quote-part Salaries</p>
                  <p class="text-caption grey--text">Mois precedent</p>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section Détails Financiers -->
        <v-card class="mb-4" outlined v-if="companyFound">
          <v-card-title class="success white--text">
            <v-icon left>mdi-calculator</v-icon>
            Détails Financiers
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <!-- Transactions Mois Actuel -->
              <v-col cols="12" md="5">
                <!-- Titre du mois avec mise en évidence améliorée -->
                <v-card elevation="0" class="mb-4" color="primary lighten-5">
                  <v-card-text class="d-flex align-center justify-center pa-3">
                    <v-icon left color="primary">mdi-calendar-today</v-icon>
                    <h3 class="primary--text text-h5 font-weight-bold mb-0">{{ getCurrentMonthName() }}</h3>
                  </v-card-text>
                </v-card>

                <v-row>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="primary">mdi-receipt</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.nbrAchatsSiteCompanyActual }}</div>
                        <div class="text-caption">Nbr Achats Site</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="success">mdi-cash</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.sumPriceTransactionsActualMonthCompanySite }}</div>
                        <div class="text-caption">Total Achats Site</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row class="mt-2">
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="orange">mdi-shopping</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.nbrCmdsInternetCompanyActual }}</div>
                        <div class="text-caption">Nbr Cmd Internet</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="purple">mdi-web</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.sumPriceOrderItemsActualMonthCompany }}</div>
                        <div class="text-caption">Total Cmd Internet</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>

              <!-- Colonne séparatrice avec ligne verticale -->
              <v-col cols="12" md="2" class="d-none d-md-flex align-center justify-center">
                <div style="width: 2px; height: 80%; background-color: #E0E0E0;"></div>
              </v-col>

              <!-- Divider horizontal sur mobile uniquement -->
              <v-col cols="12" class="d-md-none py-2">
                <v-divider></v-divider>
              </v-col>

              <!-- Transactions Mois Precedent -->
              <v-col cols="12" md="5">
                <!-- Titre du mois avec mise en évidence améliorée -->
                <v-card elevation="0" class="mb-4" color="brown lighten-5">
                  <v-card-text class="d-flex align-center justify-center pa-3">
                    <v-icon left color="brown">mdi-calendar-clock</v-icon>
                    <h3 class="brown--text text-h5 font-weight-bold mb-0">{{ getPreviousMonthName() }}</h3>
                  </v-card-text>
                </v-card>

                <v-row>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="primary">mdi-receipt</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.nbrAchatsSiteCompanyPrevious }}</div>
                        <div class="text-caption">Nbr Achats Site</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="success">mdi-cash</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.sumPriceTransactionsPreviousMonthCompanySite }}</div>
                        <div class="text-caption">Total Achats Site</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row class="mt-2">
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="orange">mdi-shopping</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.nbrCmdsInternetCompanyPrevious }}</div>
                        <div class="text-caption">Nbr Cmd Internet</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="purple">mdi-web</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.sumPriceOrderItemsPreviousMonthCompany }}</div>
                        <div class="text-caption">Total Cmd Internet</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section Encours Sens NumeriK (NOUVELLE SECTION AJOUTÉE) -->
        <v-card class="mb-4" outlined v-if="companyFound">
          <v-card-title class="warning white--text">
            <v-icon left>mdi-bank-transfer</v-icon>
            Encours Sens NumeriK
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <!-- Encours Actuel -->
              <v-col cols="12" md="6">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #FF5722;">
                  <v-icon size="48" color="deep-orange">mdi-bank-transfer</v-icon>
                  <h2 class="mt-3">{{ sensNumerikInfos.actualMonthEncours }}</h2>
                  <p class="text-subtitle-2 grey--text">Encours dû à Sens NumeriK</p>
                  <p class="text-caption grey--text">{{ getCurrentMonthName() }}</p>
                </v-card>
              </v-col>

              <!-- Encours Precedent -->
              <v-col cols="12" md="6">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #E91E63;">
                  <v-icon size="48" color="pink">mdi-bank-transfer-out</v-icon>
                  <h2 class="mt-3">{{ sensNumerikInfos.previousMonthEncours }}</h2>
                  <p class="text-subtitle-2 grey--text">Encours dû à Sens NumeriK</p>
                  <p class="text-caption grey--text">{{ getPreviousMonthName() }}</p>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Filtres et controles -->
        <v-card class="mb-4" outlined v-if="companyFound">
          <v-card-text class="pa-4">
            <v-row align="center">
              <v-col cols="12" sm="6" md="4">
                <v-select
                    v-model="depthVal"
                    :items="selectDates"
                    item-text="name"
                    item-value="value"
                    label="Profondeur"
                    outlined
                    dense
                    return-object
                    @change="depthDate"
                ></v-select>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-select
                    v-model="heightVal"
                    :items="selectDatesHeight"
                    item-text="name"
                    item-value="value"
                    label="Hauteur"
                    outlined
                    dense
                    return-object
                    @change="heightDate"
                ></v-select>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                    v-model="searchName"
                    label="Rechercher..."
                    prepend-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    @click:clear="cleared"
                    @input="search"
                    placeholder="Nom, entreprise, matricule, montant..."
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Tableau des transactions -->
        <v-data-table
            v-if="companyFound"
            :headers="headers"
            :items="filteredTransactions"
            :items-per-page="15"
            class="elevation-1"
            :loading="loading"
            loading-text="Chargement des données..."
            disable-sort
        >
          <template v-slot:[`item.entreprise`]="{ item }">
            {{ item.entreprise }}
          </template>

          <template v-slot:[`item.employe`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" class="mr-3" :color="getEmployeeColor(item.matricule)">
                <span class="white--text text-caption font-weight-bold">
                  {{ getInitials(item.nom, item.prenom) }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-bold">{{ item.nom }} {{ item.prenom }}</div>
                <div class="text-caption grey--text">{{ item.matricule }}</div>
              </div>
            </div>
          </template>

          <template v-slot:[`item.dateCommande`]="{ item }">
            <v-chip
                small
                color="primary"
                dark
            >
              {{ convertDate(item.dateCommande) }}
            </v-chip>
          </template>

          <template v-slot:[`item.montant`]="{ item }">
            <div class="text-right font-weight-bold">
              {{ item.montant }} FCFA
            </div>
          </template>

          <template v-slot:[`item.statut`]="{ item }">
            <v-chip
                small
                :color="getStatusColor(item.statut)"
                dark
            >
              {{ item.statut }}
            </v-chip>
          </template>

          <template v-slot:[`item.niveau`]="{ item }">
            <v-chip
                small
                :color="getNiveauColor(item.niveau)"
                :outlined="item.niveau !== 'PRINCIPAL'"
                :dark="item.niveau === 'PRINCIPAL'"
            >
              {{ formatNiveau(item.niveau) }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
                icon
                small
                @click="viewTransactionDetails(item)"
                color="primary"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-btn
                v-if="canDelete(item) && isValidStatusForCancellation(item.statut)"
                icon
                small
                color="error"
                @click="deleteTransaction(item)"
                :disabled="!isValidStatusForCancellation(item.statut)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog pour les détails de transaction -->
    <v-dialog v-model="detailsDialog" max-width="350">
      <v-card>
        <v-card-title class="headline white--text" style="background-color: #607D8B;">
          <v-icon left dark>mdi-receipt</v-icon>
          Détails de la transaction
        </v-card-title>
        <v-card-text class="pt-4" v-if="selectedTransaction">
          <v-row>
            <v-col cols="12">
              <div v-html="transactionDetailsHtml"></div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailsDialog = false">
            Fermer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog pour les alertes -->
    <v-dialog v-model="alertDialog" max-width="500">
      <v-card>
        <v-card-title :class="alertColor === 'success' ? 'success' : 'error'" class="white--text">
          {{ alertTitle }}
        </v-card-title>
        <v-card-text class="pt-4">
          {{ alertMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="alertDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading overlay -->
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import apiClient from "../../../config/api.js";
import * as XLSX from "xlsx";
import moment from "moment";
import {depthDate} from "../../../helper/profondeur";
import {heightDate} from "../../../helper/hauteur";

export default {
  name: "RapportsEntrepriseAdmin",
  data() {
    return {
      loading: false,
      exportLoading: false,
      alertDialog: false,
      alertTitle: "",
      alertMessage: "",
      alertColor: "info",
      detailsDialog: false,
      selectedTransaction: null,
      companyFound: false,

      // NOUVELLE PROPRIÉTÉ AJOUTÉE : Prix unitaire et encours Sens NumeriK
      unitPriceTransaction: 0,
      sensNumerikInfos: {
        actualMonthEncours: "Chargement...",
        previousMonthEncours: "Chargement..."
      },

      // Regles de validation
      rules: {
        required: value => !!value || 'Ce champ est requis'
      },

      // Données des transactions
      transactions: [],
      searchName: "",

      // Selects pour les dates
      selectDates: depthDate,
      selectDatesHeight: heightDate,
      depthVal: {
        name: "Profondeur Aujourd'hui",
        value: "0"
      },
      heightVal: {
        name: "Hauteur Aujourd'hui",
        value: "0"
      },

      // Informations entreprise et restaurant
      balanceInfos: {
        matricule: "",
        companyName: "",
        companyId: "",
        restaurantName: "",
        restaurantId: "",
        globalRevenueActualMonth: 0,
        amountDueByCompanyActualMonth: 0,
        amountAlreadyPaidByEmployeeActualMonth: 0,
        globalRevenuePreviousMonth: 0,
        amountDueByCompanyPreviousMonth: 0,
        amountAlreadyPaidByEmployeePreviousMonth: 0,
        quotePartCompanyTotalActual: 0,
        quotePartSalarialeActual: 0,
        quotePartCompanyTotalPrevious: 0,
        quotePartSalarialePrevious: 0,
        onlyCompaniesListFull: [],
        onlyCompaniesNamesList: [],
        onlyRestaurantsListFull: [],
        onlyRestaurantsNamesList: [],
        onlyCompanyNameSelect: "TSA",
        onlyRestaurantNameSelect: "Ndellice"
      },

      financialDetails: {
        nbrAchatsSiteCompanyActual: 0,
        nbrAchatsSiteCompanyPrevious: 0,
        nbrCmdsInternetCompanyActual: 0,
        nbrCmdsInternetCompanyPrevious: 0,
        sumPriceTransactionsActualMonthCompanySite: 0,
        sumPriceTransactionsPreviousMonthCompanySite: 0,
        sumPriceOrderItemsActualMonthCompany: 0,
        sumPriceOrderItemsPreviousMonthCompany: 0,
        monthActual: "",
        monthPrevious: ""
      },

      // Headers du tableau
      headers: [
        {text: "Entreprise", value: "entreprise", align: "start"},
        {text: "Employe", value: "employe", align: "start"},
        {text: "Date Commande", value: "dateCommande", align: "center"},
        {text: "Montant", value: "montant", align: "end"},
        {text: "Type Operation", value: "typeOperation", align: "center"},
        {text: "Paiement", value: "paiement", align: "center"},
        {text: "Statut", value: "statut", align: "center"},
        {text: "Niveau", value: "niveau", align: "center"},
        {text: "Actions", value: "actions", align: "center", sortable: false, width: "120px"}
      ]
    };
  },

  computed: {
    canSearch() {
      return !this.isEmpty(this.balanceInfos.onlyRestaurantNameSelect) &&
          !this.isEmpty(this.balanceInfos.onlyCompanyNameSelect) &&
          this.balanceInfos.onlyRestaurantNameSelect !== '' &&
          this.balanceInfos.onlyCompanyNameSelect !== '';
    },

    filteredTransactions() {
      let filtered = [...this.transactions];

      // Filtrage par recherche
      if (this.searchName && this.searchName.trim() !== '') {
        const query = this.searchName.toLowerCase().trim();
        filtered = filtered.filter(transaction => {
          return (
              // Recherche dans l'entreprise
              (transaction.entreprise && transaction.entreprise.toLowerCase().includes(query)) ||
              // Recherche dans le nom
              (transaction.nom && transaction.nom.toLowerCase().includes(query)) ||
              // Recherche dans le prenom
              (transaction.prenom && transaction.prenom.toLowerCase().includes(query)) ||
              // Recherche dans le nom complet
              (transaction.nom && transaction.prenom &&
                  `${transaction.nom} ${transaction.prenom}`.toLowerCase().includes(query)) ||
              // Recherche dans le matricule
              (transaction.matricule && transaction.matricule.toLowerCase().includes(query)) ||
              // Recherche dans la date de commande
              (transaction.dateCommande && transaction.dateCommande.toLowerCase().includes(query)) ||
              // Recherche dans le montant (conversion en string)
              (transaction.montant && transaction.montant.toString().includes(query)) ||
              // Recherche dans le type d'operation
              (transaction.typeOperation && transaction.typeOperation.toLowerCase().includes(query)) ||
              // Recherche dans le moyen de paiement
              (transaction.paiement && transaction.paiement.toLowerCase().includes(query)) ||
              // Recherche dans le statut
              (transaction.statut && transaction.statut.toLowerCase().includes(query)) ||
              // Recherche dans les informations supplementaires
              (transaction.infos && transaction.infos.toLowerCase().includes(query)) ||
              // Recherche dans l'origine
              (transaction.origine && transaction.origine.toLowerCase().includes(query)) ||
              // Recherche dans le niveau (formaté)
              (transaction.niveau && this.formatNiveau(transaction.niveau).toLowerCase().includes(query))
          );
        });
      }

      return filtered;
    },

    formatDateWithTime(date) {
      if (!date || date === "0") return "N/A";

      let parsedDate;
      if (typeof date === 'string' && date.includes(' ')) {
        parsedDate = moment(date, "YYYY-MM-DD HH:mm:ss");
      } else {
        parsedDate = moment(date);
      }

      return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY HH:mm") : "Date invalide";
    },

    transactionDetailsHtml() {
      if (!this.selectedTransaction) return "";

      const t = this.selectedTransaction;
      return `
        <strong style="color: #607D8B;">🏢 Entreprise</strong><br>
        ${t.entreprise}<br><br>

        <strong style="color: #607D8B;">👤 Employe</strong><br>
        ${t.nom} ${t.prenom} (${t.matricule})<br><br>

        <strong style="color: #607D8B;">📅 Date Commande</strong><br>
        ${this.convertDate(t.dateCommande)}<br><br>

        <strong style="color: #1976D2;">🕒 Date Transaction</strong><br>
        ${this.formatDateWithTime(t.dateTransaction)}<br><br>

        <strong style="color: #607D8B;">💰 Montant</strong><br>
        <span style="font-size: 16px; font-weight: bold; color: #E65100;">${t.montant} FCFA</span><br><br>

        <strong style="color: #607D8B;">🔧 Type Operation</strong><br>
        ${t.typeOperation}<br><br>

        <strong style="color: #607D8B;">💳 Moyen de Paiement</strong><br>
        ${t.paiement}<br><br>

        <strong style="color: #607D8B;">✅ Statut</strong><br>
        <span style="color: ${this.getStatusColorHex(t.statut)}; font-weight: bold;">${t.statut}</span><br><br>

        <strong style="color: #607D8B;">📊 Niveau</strong><br>
        ${this.formatNiveau(t.niveau)}<br><br>

        <strong style="color: #607D8B;">🌐 Origine</strong><br>
        ${t.origine || 'N/A'}<br><br>

        ${t.infos && t.infos !== 'null' ? `<strong style="color: #607D8B;">ℹ️ Informations</strong><br>${t.infos}<br><br>` : ''}

        <hr style="margin: 15px 0;">
        <small style="color: #757575;">
          Transaction ID: ${t.transactionUid}<br>
          Reference: #${t.id}
        </small>
      `;
    }
  },

  created() {
    this.balanceInfos.matricule = sessionStorage.getItem("userId");
    this.balanceInfos.companyName = sessionStorage.getItem("companyName");
    this.balanceInfos.onlyCompaniesListFull = JSON.parse(sessionStorage.getItem("onlyCompaniesListFull") || '[]');
    this.balanceInfos.onlyCompaniesNamesList = JSON.parse(sessionStorage.getItem("onlyCompaniesNamesList") || '[]');
    this.balanceInfos.onlyRestaurantsListFull = JSON.parse(sessionStorage.getItem("onlyRestaurantsListFull") || '[]');
    this.balanceInfos.onlyRestaurantsNamesList = JSON.parse(sessionStorage.getItem("onlyRestaurantsNamesList") || '[]');
  },

  methods: {
    isValidStatusForCancellation(status, type = 'transaction') {
      if (!status) return false;

      // Normalize status comparison
      const normalizedStatus = status.toString().toUpperCase();
      return normalizedStatus === 'SUCCESS';
    },

    async refreshData() {
      if (!this.companyFound) {
        this.showAlert("warning", "Aucune entreprise selectionnée", "Veuillez d'abord rechercher une entreprise");
        return;
      }

      this.searchName = "";
      this.depthVal = {name: "Profondeur Aujourd'hui", value: "0"};
      this.heightVal = {name: "Hauteur Aujourd'hui", value: "0"};

      await this.loadData();
      this.showAlert("success", "Actualisation reussie", "Les données ont ete mises a jour");
      this.$vuetify.goTo(0);
    },

    async searchCompany() {
      if (!this.canSearch) {
        this.showAlert("warning", "Données invalides", "Veuillez selectionner un restaurant et une entreprise.");
        return;
      }

      this.loading = true;
      this.companyFound = false;

      try {
        // Trouver les IDs
        let itemPreviousCompany = "";
        this.balanceInfos.onlyCompaniesListFull.forEach(item => {
          if (item === this.balanceInfos.onlyCompanyNameSelect) {
            this.balanceInfos.companyId = itemPreviousCompany;
          }
          itemPreviousCompany = item;
        });

        let itemPreviousRestaurant = "";
        this.balanceInfos.onlyRestaurantsListFull.forEach(item => {
          if (item === this.balanceInfos.onlyRestaurantNameSelect) {
            this.balanceInfos.restaurantId = itemPreviousRestaurant;
          }
          itemPreviousRestaurant = item;
        });

        this.companyFound = true;
        await this.loadData();
        this.showAlert("success", "Entreprise trouvée", "Les données de l'entreprise ont ete chargées avec succes");
      } catch (error) {
        this.showAlert("error", "Erreur", "Erreur lors de la recherche de l'entreprise");
        console.error("Erreur lors de la recherche:", error);
      } finally {
        this.loading = false;
      }
    },

    async loadData() {
      if (!this.companyFound) return;

      this.loading = true;

      try {
        await this.getDetailsFinancialsDatas();
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        this.showAlert("error", "Erreur de chargement", "Impossible de charger les données");
      } finally {
        this.loading = false;
      }
    },

    async getDetailsFinancialsDatas() {
      const endDate = moment().add(this.heightVal.value, "days").format("YYYY-MM-DD");
      const startDate = moment().subtract(this.depthVal.value, "days").format("YYYY-MM-DD");

      const url = `${this.$store.getters.user.baseUrl}/wallet/get_wallet_restaurant_transactions_by_company_short/${startDate}/${endDate}/${this.balanceInfos.restaurantId}/${this.balanceInfos.companyId}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);
        if (response.data.status === 200) {
          const data = response.data.datas || [];

          this.transactions = data.map(item => ({
            id: item.id,
            entreprise: this.balanceInfos.onlyCompanyNameSelect,
            nom: item.employee.accountLastname,
            prenom: item.employee.accountFirstname,
            matricule: item.employee.userId,
            dateCommande: item.dateCommand,
            montant: item.price,
            typeOperation: item.typeTrs,
            paiement: item.typeAuth,
            statut: item.status,
            infos: item.infos,
            origine: item.orderOrigin,
            niveau: item.level,
            motifRefus: item.motifFailed,
            dateTransaction: item.dateTransaction,
            transactionUid: item.transaction_uid
          }));

          await this.getFinancialInfosByCompany();
        } else {
          this.showAlert("warning", "Avertissement", response.data.message);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des transactions:", error);
      }
    },

    async getFinancialInfosByCompany() {
      const url = `${this.$store.getters.user.baseUrl}/wallet/get_financial_infos_company/${this.balanceInfos.restaurantId}/${this.balanceInfos.companyId}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);
        if (response.data.status === 200) {
          const data = response.data.datas;

          // Mise a jour des données financieres
          Object.assign(this.financialDetails, data);

          // Capitalisation des mois
          String.prototype.capitalize = function (allWords) {
            return (allWords) ?
                this.split(' ').map(word => word.capitalize()).join(' ') :
                this.charAt(0).toUpperCase() + this.slice(1);
          }

          this.financialDetails.monthActual = data.monthActual.toLowerCase().capitalize(true) + " - " + data.yearActual;
          this.financialDetails.monthPrevious = data.monthPrevious.toLowerCase().capitalize(true) + " - " + data.yearPrevious;

          // Mise a jour des balances
          this.balanceInfos.globalRevenueActualMonth = data.globalRevenueActualMonth;
          this.balanceInfos.amountDueByCompanyActualMonth = data.amountDueByCompanyActualMonth;
          this.balanceInfos.amountAlreadyPaidByEmployeeActualMonth = data.amountAlreadyPaidByEmployeeActualMonth;
          this.balanceInfos.globalRevenuePreviousMonth = data.globalRevenuePreviousMonth;
          this.balanceInfos.amountDueByCompanyPreviousMonth = data.amountDueByCompanyPreviousMonth;
          this.balanceInfos.amountAlreadyPaidByEmployeePreviousMonth = data.amountAlreadyPaidByEmployeePreviousMonth;
          this.balanceInfos.quotePartCompanyTotalActual = data.quotePartCompanyTotalActual;
          this.balanceInfos.quotePartSalarialeActual = data.quotePartSalarialeActual;
          this.balanceInfos.quotePartCompanyTotalPrevious = data.quotePartCompanyTotalPrevious;
          this.balanceInfos.quotePartSalarialePrevious = data.quotePartSalarialePrevious;

          // NOUVEAU : Après avoir récupéré les informations financières, récupérer le prix unitaire
          this.getUnitPriceTransaction();
        }
      } catch (error) {
        console.error("Erreur lors du chargement des infos financieres:", error);
      }
    },

    // NOUVELLE MÉTHODE AJOUTÉE : Récupérer le prix unitaire et calculer les encours
    getUnitPriceTransaction() {
      let str = `${this.$store.getters.user.baseUrl}/wallet/get_unit_price_transaction/${this.balanceInfos.companyId}/${sessionStorage.getItem("accountId")}`;

      apiClient
          .get(str)
          .then(response => {
            if (response.data.status === 200) {
              this.unitPriceTransaction = response.data.datas;

              // Calcul pour le mois en cours
              const actualTotal =
                  (parseFloat(this.financialDetails.nbrAchatsSiteCompanyActual) || 0) +
                  (parseFloat(this.financialDetails.nbrCmdsInternetCompanyActual) || 0);

              // Calcul pour le mois précédent
              const previousTotal =
                  (parseFloat(this.financialDetails.nbrAchatsSiteCompanyPrevious) || 0) +
                  (parseFloat(this.financialDetails.nbrCmdsInternetCompanyPrevious) || 0);

              // Calculer les encours
              const actualEncours = actualTotal * this.unitPriceTransaction;
              const previousEncours = previousTotal * this.unitPriceTransaction;

              // Formater les valeurs avec séparateur de milliers
              this.sensNumerikInfos.actualMonthEncours = this.formatNumberWithCommas(actualEncours) + " F CFA";
              this.sensNumerikInfos.previousMonthEncours = this.formatNumberWithCommas(previousEncours) + " F CFA";
            } else {
              this.sensNumerikInfos.actualMonthEncours = "Erreur de chargement";
              this.sensNumerikInfos.previousMonthEncours = "Erreur de chargement";
              this.showAlert('warning', "Erreur", response.data.message);
            }
          })
          .catch(() => {
            this.sensNumerikInfos.actualMonthEncours = "Erreur de chargement";
            this.sensNumerikInfos.previousMonthEncours = "Erreur de chargement";
          });
    },

    // NOUVELLE MÉTHODE AJOUTÉE : Formater les nombres avec séparateur de milliers
    formatNumberWithCommas(number) {
      return number.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },

    depthDate() {
      if (this.depthVal) {
        sessionStorage.setItem("depthValChoosedd", this.depthVal.value);
        sessionStorage.setItem("depthValChoosedt", this.depthVal.name);
      }
      this.getDetailsFinancialsDatas();
    },

    heightDate() {
      if (this.heightVal) {
        sessionStorage.setItem("heightValChoosedd", this.heightVal.value);
        sessionStorage.setItem("heightValChoosedt", this.heightVal.name);
      }
      this.getDetailsFinancialsDatas();
    },

    cleared() {
      this.searchName = "";
    },

    search() {
      // La recherche est maintenant automatique via le computed filteredTransactions
    },

    async exportToExcel() {
      this.exportLoading = true;

      try {
        const dataToExport = [];

        // En-tetes
        dataToExport.push([
          "Entreprise",
          "Nom",
          "Prenom",
          "Matricule",
          "Date Commande",
          "Montant (FCFA)",
          "Type Operation",
          "Moyen de Paiement",
          "Statut",
          "Niveau",
          "Origine",
          "Informations",
          "Date Transaction"
        ]);

        // Données
        this.filteredTransactions.forEach(item => {
          dataToExport.push([
            item.entreprise,
            item.nom,
            item.prenom,
            item.matricule,
            this.convertDate(item.dateCommande),
            item.montant,
            item.typeOperation,
            item.paiement,
            item.statut,
            this.formatNiveau(item.niveau),
            item.origine || '',
            item.infos || '',
            this.convertDate(item.dateTransaction)
          ]);
        });

        // Creation du fichier Excel
        const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();

        // Ajuster les largeurs de colonnes
        const columnWidths = [
          {wch: 15}, {wch: 15}, {wch: 15}, {wch: 12},
          {wch: 15}, {wch: 12}, {wch: 15}, {wch: 15},
          {wch: 10}, {wch: 12}, {wch: 12}, {wch: 20}, {wch: 18}
        ];
        worksheet['!cols'] = columnWidths;

        XLSX.utils.book_append_sheet(workbook, worksheet, "Rapports Entreprise");

        // Nom du fichier
        const currentDate = new Date().toISOString().split('T')[0];
        const fileName = `Rapports_Entreprise_${this.balanceInfos.onlyCompanyNameSelect}_${this.balanceInfos.onlyRestaurantNameSelect}_${currentDate}.xlsx`;

        XLSX.writeFile(workbook, fileName);

        this.showAlert("success", "Export reussi", `Le fichier ${fileName} a ete telecharge avec succes`);

      } catch (error) {
        console.error("Erreur lors de l'export Excel:", error);
        this.showAlert("error", "Erreur d'export", "Impossible d'exporter les données vers Excel");
      } finally {
        this.exportLoading = false;
      }
    },

    viewTransactionDetails(transaction) {
      this.selectedTransaction = transaction;
      this.detailsDialog = true;
    },

    canDelete(transaction) {
      // Only admin can delete
      if (sessionStorage.getItem("userId")?.toLowerCase() !== "admin") {
        return false;
      }

      // Only allow deletion if status is SUCCESS (case-insensitive)
      const status = transaction.statut?.toLowerCase();
      if (status !== "success") {
        return false;
      }

      // Don't allow deletion of internet transactions
      const origin = transaction.origine?.toLowerCase();
      if (origin === "internet") {
        return false;
      }

      return true;
    },

    async deleteTransaction(transaction) {
      // Double-check permissions before deletion
      if (!this.canDelete(transaction)) {
        this.showAlert("error", "Action non autorisée", "Vous n'avez pas les droits pour supprimer cette transaction ou elle ne peut pas être supprimée.");
        return;
      }

      // Additional status verification
      if (transaction.statut?.toLowerCase() !== "success") {
        this.showAlert("error", "Statut invalide", "Seules les transactions avec le statut 'SUCCESS' peuvent être supprimées.");
        return;
      }

      if (confirm("Êtes-vous sûr de vouloir supprimer cette transaction ?")) {
        this.loading = true;
        try {
          const url = `${this.$store.getters.user.baseUrl}/wallet/delete_wallet_transaction_by_uuid/${transaction.transactionUid}/${sessionStorage.getItem("accountId")}`;
          const response = await apiClient.delete(url);

          if (response.data.status === 200) {
            this.showAlert("success", "Succès", response.data.message);
            await this.getDetailsFinancialsDatas();
          } else {
            this.showAlert("error", "Erreur", response.data.message);
          }
        } catch (error) {
          this.showAlert("error", "Erreur", "Impossible de supprimer la transaction");
        } finally {
          this.loading = false;
        }
      }
    },

    // Methodes utilitaires
    isEmpty(str) {
      return (str === null || !str || 0 === str.length || /^\s*$/.test(str));
    },

    getInitials(nom, prenom) {
      const n = nom ? nom.charAt(0).toUpperCase() : "";
      const p = prenom ? prenom.charAt(0).toUpperCase() : "";
      return n + p;
    },

    getEmployeeColor(matricule) {
      const colors = ['#607D8B', '#388E3C', '#F57C00', '#7B1FA2', '#D32F2F', '#00796B', '#5D4037', '#455A64'];
      const index = matricule ? matricule.length % colors.length : 0;
      return colors[index];
    },

    getStatusColor(status) {
      switch (status?.toLowerCase()) {
        case "success":
          return "success";
        case "failed":
          return "error";
        case "cancelled":
          return "warning";
        default:
          return "grey";
      }
    },

    getStatusColorHex(status) {
      switch (status?.toLowerCase()) {
        case "success":
          return "#4CAF50";
        case "failed":
          return "#F44336";
        case "cancelled":
          return "#FF9800";
        default:
          return "#9E9E9E";
      }
    },

    getNiveauColor(niveau) {
      switch (niveau) {
        case 'PRINCIPAL':
          return 'primary';
        case 'SECONDAIRE_COMPANY':
          return 'orange';
        case 'SECONDAIRE_PERSONNEL':
          return 'purple';
        default:
          return 'grey';
      }
    },

    formatNiveau(niveau) {
      switch (niveau) {
        case 'PRINCIPAL':
          return 'PRINCIPAL';
        case 'SECONDAIRE_COMPANY':
          return 'ENTREPRISE';
        case 'SECONDAIRE_PERSONNEL':
          return 'PERSONNEL';
        default:
          return niveau || 'N/A';
      }
    },

    getCurrentMonthName() {
      return this.financialDetails.monthActual || "Mois Actuel";
    },

    getPreviousMonthName() {
      return this.financialDetails.monthPrevious || "Mois Precedent";
    },

    convertDate(val) {
      if (!val) return "";
      return moment(val).format("DD-MM-YYYY");
    },

    showAlert(color, title, message) {
      this.alertColor = color;
      this.alertTitle = title;
      this.alertMessage = message;
      this.alertDialog = true;
    }
  }
};
</script>

<style scoped>
.v-card-title {
  font-weight: 600;
  font-size: 1.1rem;
}

.v-data-table {
  font-size: 0.9rem;
}

/* Style pour les en-tetes de colonnes */
.v-data-table >>> thead tr th {
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  color: #607D8B !important;
  background-color: #F5F5F5 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px !important;
}

/* Ajouter une bordure sous les en-tetes */
.v-data-table >>> thead tr {
  border-bottom: 2px solid #E0E0E0;
}

/* Style au survol des en-tetes */
.v-data-table >>> thead tr th:hover {
  background-color: #EEEEEE !important;
  cursor: pointer;
}

/* Amelioration de l'apparence des cartes */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
}

/* Style pour les statistiques */
.text-caption {
  font-size: 0.75rem !important;
  color: #666;
}

/* Responsive */
@media (max-width: 600px) {
  .v-card-title {
    font-size: 1rem;
  }

  h2 {
    font-size: 1.5rem !important;
  }
}

/* Animation pour les chips */
.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
}
</style>